import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, ChevronLeft, ChevronRight, Images, Loader2, X } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ServiceFaqSection } from "@/components/ServiceFaqSection";
import { toast } from "sonner";

const STYLES = ["Press shots", "Cover art", "Studio stills", "Lifestyle", "Event / BTS", "Other"];
const TIME_SLOTS = ["Morning (10:00 AM)", "Midday (1:00 PM)", "Afternoon (4:00 PM)", "Evening (7:00 PM)"];

const GalleryViewer = ({ gallery, onClose }) => {
  const [idx, setIdx] = useState(0);
  const photos = gallery?.photos || [];

  useEffect(() => {
    if (!gallery) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") { e.preventDefault(); setIdx((i) => (i + 1) % photos.length); }
      if (e.key === "ArrowLeft") { e.preventDefault(); setIdx((i) => (i - 1 + photos.length) % photos.length); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gallery, photos.length, onClose]);

  if (!gallery) return null;
  const photo = photos[idx];

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex flex-col bg-black/95 p-4 backdrop-blur-sm sm:p-8"
      onClick={onClose}
      data-testid="gallery-viewer"
    >
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">{gallery.title}</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">{idx + 1} / {photos.length}</p>
        </div>
        <button onClick={onClose} data-testid="gallery-viewer-close"
          className="rounded-full border border-white/15 p-2 text-zinc-300 transition-colors hover:text-white">
          <X size={18} />
        </button>
      </div>

      <div className="relative mx-auto flex w-full max-w-5xl flex-1 items-center justify-center overflow-hidden py-4" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={photo?.url + idx}
            src={fileUrl(photo?.url)}
            alt={photo?.caption || gallery.title}
            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25 }}
            className="max-h-full max-w-full rounded-2xl object-contain"
            data-testid="gallery-viewer-image"
          />
        </AnimatePresence>
        <button
          onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
          data-testid="gallery-viewer-prev"
          className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-3 text-white backdrop-blur-md transition-colors hover:bg-emerald-300 hover:text-black"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => setIdx((i) => (i + 1) % photos.length)}
          data-testid="gallery-viewer-next"
          className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-3 text-white backdrop-blur-md transition-colors hover:bg-emerald-300 hover:text-black"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {photo?.caption && (
        <p className="mx-auto max-w-3xl pb-3 text-center text-sm text-zinc-400" data-testid="gallery-viewer-caption">
          {photo.caption}
        </p>
      )}

      <div className="mx-auto flex w-full max-w-5xl gap-2 overflow-x-auto pb-1" data-testid="gallery-viewer-thumbs">
        {photos.map((p, i) => (
          <button key={p.url + i} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
            data-testid={`gallery-thumb-${i}`}
            className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border transition-all ${
              i === idx ? "border-emerald-300" : "border-white/10 opacity-50 hover:opacity-90"
            }`}>
            <img src={fileUrl(p.url)} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </motion.div>
  );
};

export default function Photography() {
  const [galleries, setGalleries] = useState([]);
  const [open, setOpen] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", shoot_for: "", style: STYLES[0],
    examples: "", location: "", date: "", time_slot: TIME_SLOTS[0], notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/galleries").then((r) => setGalleries(r.data)).catch(() => {});
    api.get("/photography/booked-dates").then((r) => setBookedDates(r.data.dates || [])).catch(() => {});
  }, []);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const dateTaken = form.date && bookedDates.includes(form.date);

  const submit = async (e) => {
    e.preventDefault();
    if (dateTaken) return;
    setBusy(true);
    try {
      const { data } = await api.post("/photography/intake", { ...form, origin_url: window.location.origin });
      toast.success("Intake received — complete the $50 deposit to lock your date");
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || "Could not start the booking — try again");
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-white/10 bg-[#0B0B0E] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-emerald-300/60";
  const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500";

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="photography-page">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Visuals &amp; photo work</p>
        <h1 className="font-heading mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Photography</h1>
        <p className="mt-4 max-w-xl text-sm text-zinc-400 sm:text-base">
          Press shots, cover art, studio stills and lifestyle sets — shot in Atlanta. Browse recent client work below,
          then tell me about your shoot and lock your date with a $50 deposit.
        </p>
      </Reveal>

      <section className="mt-14" data-testid="photography-galleries">
        <Reveal>
          <div className="flex items-center gap-3">
            <Images size={16} className="text-emerald-300" />
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Client Galleries</h2>
          </div>
        </Reveal>
        {galleries.length > 0 ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="photography-gallery-grid">
            {galleries.map((g, i) => (
              <Reveal key={g.id} delay={(i % 3) * 0.08}>
                <button
                  onClick={() => setOpen(g)}
                  data-testid={`gallery-card-${g.id.slice(0, 8)}`}
                  className="group relative block w-full overflow-hidden rounded-2xl border border-white/10 text-left transition-colors hover:border-emerald-300/40"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-black">
                    <img
                      src={fileUrl(g.photos[0]?.url)} alt={g.title} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <span className="absolute right-3 top-3 rounded-full bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-emerald-200 backdrop-blur-md">
                      {g.photos.length} photos
                    </span>
                  </div>
                  <div className="bg-[#141419] p-4">
                    <p className="font-heading text-sm font-bold text-white">{g.title}</p>
                    {g.caption && <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{g.caption}</p>}
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        ) : (
          <p className="mt-8 rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-600" data-testid="photography-galleries-empty">
            Galleries are being curated — check back soon.
          </p>
        )}
      </section>

      <section className="mt-20" data-testid="photography-intake">
        <Reveal>
          <div className="flex items-center gap-3">
            <Camera size={16} className="text-emerald-300" />
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Book a Shoot</h2>
          </div>
          <p className="mt-2 max-w-lg text-sm text-zinc-400">
            Fill this out and pay the $50 deposit — your date locks onto the studio calendar the moment it clears.
            The deposit comes off your final rate.
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <form onSubmit={submit} className="mt-8 rounded-3xl border border-white/10 bg-[#141419] p-6 sm:p-10" data-testid="photo-intake-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className={labelCls}>Your name *</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls} placeholder="Artist / client name" data-testid="intake-name" />
              </label>
              <label className="block">
                <span className={labelCls}>Email *</span>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls} placeholder="you@email.com" data-testid="intake-email" />
              </label>
              <label className="block">
                <span className={labelCls}>Phone</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputCls} placeholder="(404) 555-0123" data-testid="intake-phone" />
              </label>
              <label className="block">
                <span className={labelCls}>Shoot style *</span>
                <select value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })}
                  className={inputCls} data-testid="intake-style">
                  {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className={labelCls}>What are the photos for? *</span>
                <textarea required rows={2} value={form.shoot_for} onChange={(e) => setForm({ ...form, shoot_for: e.target.value })}
                  className={inputCls} placeholder="Cover art for my next single, press kit for a label pitch, IG content…"
                  data-testid="intake-shoot-for" />
              </label>
              <label className="block sm:col-span-2">
                <span className={labelCls}>Style examples / references</span>
                <textarea rows={2} value={form.examples} onChange={(e) => setForm({ ...form, examples: e.target.value })}
                  className={inputCls} placeholder="Links to looks you like, moodboards, artists whose photos you rate…"
                  data-testid="intake-examples" />
              </label>
              <label className="block">
                <span className={labelCls}>Location</span>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className={inputCls} placeholder="The lab, on-location, your spot…" data-testid="intake-location" />
              </label>
              <div className="grid grid-cols-2 gap-5">
                <label className="block">
                  <span className={labelCls}>Date *</span>
                  <input required type="date" min={today} value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className={`${inputCls} ${dateTaken ? "border-red-500/60" : ""}`} data-testid="intake-date" />
                </label>
                <label className="block">
                  <span className={labelCls}>Time *</span>
                  <select value={form.time_slot} onChange={(e) => setForm({ ...form, time_slot: e.target.value })}
                    className={inputCls} data-testid="intake-time">
                    {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
              {dateTaken && (
                <p className="text-xs text-red-400 sm:col-span-2" data-testid="intake-date-taken">
                  That date is already locked by another shoot — pick another day.
                </p>
              )}
              <label className="block sm:col-span-2">
                <span className={labelCls}>Anything else</span>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className={inputCls} placeholder="People count, props, deadlines, deliverables…" data-testid="intake-notes" />
              </label>
            </div>
            <button
              type="submit" disabled={busy || !!dateTaken}
              data-testid="intake-submit"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-4 text-xs font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 hover:shadow-[0_0_30px_rgba(110,231,183,0.3)] disabled:opacity-50"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
              Continue — $50 Deposit Locks the Date
            </button>
            <p className="mt-3 text-center text-[11px] text-zinc-600">
              Deposit is deducted from your final rate. You'll get a confirmation email when it clears.
            </p>
          </form>
        </Reveal>
      </section>

      <ServiceFaqSection
        matchTitles={["Photography"]}
        heading="Photography FAQ"
        eyebrow="Before you book"
        blurb="Quick answers to the most common questions about shoots."
        testid="photography-faq"
      />

      <AnimatePresence>
        {open && <GalleryViewer gallery={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </div>
  );
}
